import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { api, qs } from '../api';
import { cn } from '../ui';

interface Group {
  key: string;
  label: string;
  hits: { href: string; title: string; detail: string | null }[];
  more: boolean;
}

/**
 * The search box in the header, as in NetSuite: type anywhere, see matches grouped by kind of record, open one.
 * Press / to jump to it, the arrow keys to move and Enter to open.
 */
export function GlobalSearch() {
  const [text, setText] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const here = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setTerm(text.trim()), 250);
    return () => clearTimeout(t);
  }, [text]);

  // "/" focuses the box unless the person is already typing somewhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = e.target as HTMLElement | null;
      if (e.key === '/' && !e.metaKey && !e.ctrlKey && el && !/^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) && !el.isContentEditable) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    const onClick = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('mousedown', onClick);
    };
  }, []);
  useEffect(() => setOpen(false), [here.pathname, here.search]);

  const results = useQuery({
    queryKey: ['global-search', term],
    enabled: term.length >= 2,
    queryFn: () => api<{ groups: Group[] }>(`/api/v1/search${qs({ q: term })}`).then((r) => r.groups),
    placeholderData: (prev) => prev,
    staleTime: 10_000,
  });
  const groups = term.length >= 2 ? (results.data ?? []) : [];
  const flat = useMemo(() => groups.flatMap((g) => g.hits), [groups]);
  useEffect(() => setActive(0), [term]);

  const go = (href: string) => {
    setOpen(false);
    setText('');
    input.current?.blur();
    navigate(href);
  };

  let index = -1;
  return (
    <div ref={box} className="relative w-56 lg:w-72">
      <input
        ref={input}
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-label="Global search"
        placeholder="Search…  ( / )"
        autoComplete="off"
        value={text}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setText(e.target.value);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setOpen(false);
            input.current?.blur();
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((a) => Math.min(a + 1, flat.length - 1));
          } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((a) => Math.max(a - 1, 0));
          } else if (e.key === 'Enter' && flat[active]) {
            e.preventDefault();
            go(flat[active]!.href);
          }
        }}
        className="w-full rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-100 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
      />
      {open && term.length >= 2 && (
        <div className="absolute right-0 z-30 mt-1 max-h-[70vh] w-[min(28rem,90vw)] overflow-y-auto rounded-md border border-slate-200 bg-white py-1 text-sm text-slate-800 shadow-lg">
          {results.isPending ? (
            <p className="px-3 py-2 text-slate-500">Searching…</p>
          ) : groups.length === 0 ? (
            <p className="px-3 py-2 text-slate-500">Nothing found for “{term}”.</p>
          ) : (
            groups.map((g) => (
              <div key={g.key}>
                <div className="px-3 pt-2 pb-1 text-xs font-semibold tracking-wide text-slate-500 uppercase">{g.label}</div>
                {g.hits.map((h) => {
                  index += 1;
                  const i = index;
                  return (
                    <button
                      key={h.href + h.title}
                      type="button"
                      onMouseEnter={() => setActive(i)}
                      onClick={() => go(h.href)}
                      className={cn('block w-full px-3 py-1.5 text-left', active === i ? 'bg-slate-100' : 'hover:bg-slate-50')}
                    >
                      <span className="font-medium">{h.title}</span>
                      {h.detail && <span className="ml-2 text-xs text-slate-500">{h.detail}</span>}
                    </button>
                  );
                })}
                {g.more && <div className="px-3 pb-1 text-xs text-slate-400">More match; make the search more specific.</div>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
